import { ReactElement, useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import { Badge, ClickAwayListener, Grow, Paper, Popper, ListItemButton } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { useAsyncFetch } from "../../system/useFetch";
import ErrorIcon from "@mui/icons-material/Error";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DateTime } from "luxon";
import { IDownload, IResponseMessage } from "../../types";
import Detail from "../dialogs/detail";
import Markdown from "react-markdown";
import { addAlert } from "../../actions";
import { useDispatch } from "react-redux";
import { exportFile } from "../../system/utils";

const Sec30 = 30000;

export enum SeverityType {
  Low,
  Normal,
  High
}

interface IAction extends Record<string, any>{
  action: string;
}

export interface INotification {
  notificationID: number;
  subscriptionID: number;
  topicID: number;
  topic: string;
  message: string;
  action: IAction | null;
  datetime: string;
  severity: SeverityType;
  status: number;
}

interface ISeverityProps {
  severity: SeverityType;
}

let from: DateTime | null = null;



const SeverityIcon = ({ severity }: ISeverityProps) => {
  switch (severity) {
    case SeverityType.High:
      return <ErrorIcon color="error"/>;
    case SeverityType.Normal:
      return <WarningIcon color="warning"/>;
    default:
      return <InfoIcon color="info"/>;
  }
};

const getSeverityText = (severity: SeverityType) => {
  switch (severity) {
    case SeverityType.High:
      return "error";
    case SeverityType.Normal:
      return "warning";
    default:
      return "info";
  }
};

const NotificationComponent = (): ReactElement => {
  const dispatch = useDispatch<any>();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [ notifications, setNotifications ] = useState<INotification[]>([]);
  const [ notification, setNotification ] = useState<INotification | null>(null);
  const [ executeRequest ] = useAsyncFetch<INotification[]>();
  const [ executeCommand ] = useAsyncFetch<IResponseMessage>();
  const [ executeDownload ] = useAsyncFetch<IDownload>();

  useEffect(() => {
    const process = () => {
      const now = DateTime.now();
      from = from ?? now.minus({ week: 1 });

      executeRequest({
        module: "notification",
        query: "getLastUserNotifications",
        payload: {
          from: from.toFormat("yyyy-MM-dd HH:mm:ss")
        }
      }).then(response => {
        from = now;

        if (response) {
          const alerts: INotification[] = [];

          setNotifications(prevState => {
            const newArray = [ ...prevState ];

            response.forEach(n => {
              if (!newArray.find(a => a.notificationID === n.notificationID)) {
                newArray.push(n);
                alerts.push(n);
              }
            });

            return newArray.sort((a, b) => a.notificationID - b.notificationID);
          });

          alerts.forEach(n => {
            if (n.status === 0) {
              dispatch(addAlert({ message: n.message, severity: getSeverityText(n.severity), hash: `notification-${n.notificationID}` }));
            }
          });
        }
      });
    };

    const timeout = setInterval(process, Sec30);

    process();

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const onNotificationClick = (item: INotification) => {
    if (item.status === 0) {
      executeCommand({
        module: "notification",
        query: "setStatus",
        payload: {
          notificationID: item.notificationID,
          status: 1
        }
      }).then(response => {
        if (response && response.status) {
          setNotifications(notifications.map(n => {
            if (n.notificationID === item.notificationID) {
              n.status = 1;
            }

            return n;
          }));
        }
      });
    }
  };

  const onShowDetail = (item: INotification) => {
    if (item.action?.action === "INFO") {
      setNotification(item);
      onNotificationClick(item);
    } else if (item.action?.action === "DOWNLOAD" && item.action?.payload) {
      executeDownload(item.action?.payload)
        .then(response => {
          if (response) {
            exportFile(response.base64, response.filename);
          }

          if (item.status === 0) {
            onNotificationClick(item);
          }
        });
    }
  };

  return(
    <>
      <IconButton ref={anchorRef}  onClick={() => setOpen((prevOpen) => !prevOpen)}>
        <Badge badgeContent={notifications.filter(n => n.status === 0).length} color="secondary" max={10} overlap="circular">
          <NotificationsIcon/>
        </Badge>
      </IconButton>

      <Popper open={open} anchorEl={anchorRef.current} role={undefined} transition={true} disablePortal={true}>
        {({ TransitionProps, placement }) =>
          <Grow
            {...TransitionProps}
            style={{ transformOrigin: placement === "bottom" ? "center top" : "center bottom", width: "400px" }}>
            <Paper>
              <ClickAwayListener onClickAway={() => setOpen(false)}>
                <List component="nav" sx={{
                  maxHeight: window.innerHeight * .9,
                  overflowY: "auto"
                }}>
                  {notifications.length === 0 &&
                    <ListItem>
                      <ListItemText primary="No notifications"/>
                    </ListItem>
                  }

                  {notifications.map(item => {

                    return (
                      <ListItem
                        key={`not-${item.notificationID}`}
                        secondaryAction={ item.action
                          ? <IconButton onClick={() => onShowDetail(item)}>
                            {item.action.action === "INFO" && <OpenInNewIcon/>}
                            {item.action.action === "DOWNLOAD" && <DownloadForOfflineIcon/>}
                          </IconButton>
                          : undefined
                        }
                        disablePadding={true}>
                        <ListItemButton selected={item.status === 0} className="gap-2"
                          onClick={() => onNotificationClick(item)}>
                          <SeverityIcon severity={item.severity}/>
                          <ListItemText primary={item.message} secondary={item.datetime} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </ClickAwayListener>
            </Paper>
          </Grow>
        }
      </Popper>
      {notification && notification.action?.action === "INFO" &&
        <Detail closed={() => setNotification(null)} fullWidth={true} maxWidth="sm" title="Detail">
          <div style={{ maxHeight: 400 }} className="p-1">
            <Markdown>{notification.action.detail ?? ""}</Markdown>
          </div>
        </Detail>
      }
    </>
  );
};

export default NotificationComponent;
